import { FC, useState } from "react";
import { createPortal } from "react-dom";
import { useData } from "data/data-provider";
import {
  formatPrice,
  getEnabledPeriodPrices,
  splitPeriodRows,
  cardPhotoUrl,
  dailyRate,
  markupPercent,
  moreRatesLabel,
} from "@1-now/booking-core";
import DbIcon from "designs/DesignB/components/icons";
import bonzahLogo from "images/bonzah.png";
import { badgePercent, isNotBookable, isPriceOnRequest } from "utils/carPrice";
import { specChips, SpecChip } from "utils/carSpecs";
import { tierAreaHeight, TIER_MORE_PX, TIER_ROW_PX } from "utils/tierSlots";
import { useTierSlots } from "utils/useTierSlots";
import { fleetClassNames } from "utils/fleetClasses";

export interface FleetCardProps {
  className?: string;
  data?: any;
  size?: "default" | "small";
  simpleImg?: boolean;
  /** Opens (or closes) the car's details: the whole card is the control (1NOW-11219). */
  onClick?: any;
  /** This car's details are open: the card wears the accent ring and its button reads "Hide details". */
  selected?: boolean;
  /** The id of the open details, for the button's aria-controls. */
  panelId?: string;
  loading?: boolean;
}

// Design B's fleet card (1NOW-11175): the photo on a gradient band, the name, clean spec
// chips, a big price, with a hover state, a price-on-request state and a designed no-photo
// placeholder. Styled in DesignB/theme.css (.db-fc).
//
// 1NOW-11219: the card has no Book now. A click anywhere on it, or its View details button,
// opens the car's details in place (ExpandingFleetGrid), and Book now, or Request price for a
// car priced on request, is in there.
const FleetCard: FC<FleetCardProps> = ({
  className = "",
  data,
  onClick,
  selected = false,
  panelId,
}) => {
  const { ourCompany } = useData();
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });
  const {
    name,
    description,
    increased_price,
    has_bonzah_insurance,
  } = data!;
  // 1NOW-11127: FMS saves classes to fleet_classes; fleet_class is the fallback.
  const classNames = fleetClassNames(data);

  // On request, or no base price: not bookable. The renter asks for a quote
  // (on request) or is told a price is coming (unpriced).
  const notBookable = isNotBookable(data);
  const onRequest = isPriceOnRequest(data);

  // Bonzah "available" badge — parity with Design A's FleetCard. Shows only
  // when the company enabled the tag AND this fleet carries Bonzah insurance.
  const shouldShowBonzahBadge =
    ourCompany?.show_bonzah_tag === true && has_bonzah_insurance;

  const increasedPriceNum = Number(increased_price);
  // The daily rate and the markup of the crossed-out price over it, by the
  // rule every design's card follows (dailyRate / markupPercent in
  // @1-now/booking-core): the trip average, else the list price. A car priced
  // only on `price` used to show no rate here. A car with no base price
  // shows no amount at all, so it carries no markup either.
  const currentPriceNum = dailyRate(data) ?? 0;
  // 1NOW-11175: a badge only for a believable percentage; one past 90 (a stale was-price)
  // would read "-892%", so neither the badge nor the crossed-out price is drawn.
  const markup = notBookable ? null : badgePercent(markupPercent(data));
  const hasDiscount = markup != null;
  const discountPercent = markup ?? 0;

  const periodPrices = getEnabledPeriodPrices(
    currentPriceNum,
    ourCompany,
    data?.period_prices,
  );
  const primaryPeriod = periodPrices[0];
  const [tiersExpanded, setTiersExpanded] = useState(false);
  const { shown: secondaryPeriods, hiddenCount } = splitPeriodRows(
    periodPrices.slice(1),
    tiersExpanded,
  );
  // The tier area holds the room for the most tiers any card shows, so the main price and the
  // button sit at the same height in every card of a row (1NOW-11110).
  const slots = useTierSlots(data);

  // Only the specs that have a value, and no strip at all when none do
  // (1NOW-11141). Transmission is read from `transmission_type` and labelled
  // the way the package labels it ("Semi Automatic", not "Semi_automatic").
  const specs = specChips(data);

  // No photo, or one that will not load, is a designed placeholder, not a stock silhouette.
  const photoSrc = cardPhotoUrl(data);
  const [photoFailed, setPhotoFailed] = useState(false);
  const showPhoto = !!photoSrc && !photoFailed;

  const handleMouseEnterDesc = (e: React.MouseEvent) => {
    setIsHovered(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
    });
  };
  const handleMouseLeaveDesc = () => setIsHovered(false);

  return (
    <article
      onClick={onClick}
      className={`db-fc group cursor-pointer ${selected ? "is-open" : ""} ${className}`}
      data-nc-id="StayCard"
    >
      {/* The photo on its gradient band: the band darkens the foot of the picture so the badges
          and the hover strip read on any photo. */}
      <div className="db-fc__photo">
        {showPhoto ? (
          <img
            src={photoSrc!}
            alt={name}
            loading="lazy"
            onError={() => setPhotoFailed(true)}
            className="db-fc__img"
          />
        ) : (
          <div className="db-fc__placeholder">
            <DbIcon name="car" className="h-12 w-12" />
            <span>Photo coming soon</span>
          </div>
        )}
        {showPhoto && <div aria-hidden="true" className="db-fc__band" />}

        {/* Class tags, top left */}
        {classNames.length > 0 && (
          <div className="absolute left-0 top-3 z-10 flex max-w-[calc(100%-5rem)] flex-col items-start gap-1.5">
            {classNames.slice(0, 2).map((tag) => (
              <span key={tag} className="db-tag">
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Discount badge, top right */}
        {hasDiscount && <span className="db-fc__badge">-{discountPercent}%</span>}

        {/* Bonzah "available" badge — bottom-left over the band (parity with Design A). White
            pill so it stays legible on any theme. */}
        {shouldShowBonzahBadge && (
          <span className="absolute bottom-3 left-3 z-10 inline-flex items-center gap-1.5 rounded-md bg-white/90 px-2.5 py-1 text-[11px] font-semibold text-gray-800 shadow-sm backdrop-blur-sm">
            <img
              src={bonzahLogo}
              alt="Bonzah"
              className="h-4 w-4 object-contain"
              onError={(e: any) => {
                e.currentTarget.style.display = "none";
              }}
            />
            Bonzah available
          </span>
        )}

      </div>

      {/* Body */}
      <div className="db-fc__body">
        {/* Title */}
        <h3 className="db-fc__name" title={name}>
          {name}
        </h3>

        {/* Description */}
        <p
          className="db-fc__desc"
          onMouseEnter={description ? handleMouseEnterDesc : undefined}
          onMouseLeave={description ? handleMouseLeaveDesc : undefined}
        >
          {description ? description.split(" ").slice(0, 7).join(" ") : " "}
        </p>

        {/* Spec chips: left-aligned with the title and the price, wrapping when they run out
            of room, each one line ("4 seats"). Absent when the car has no specs to show
            (1NOW-11141). Only clean labels (1NOW-11175). */}
        {specs.length > 0 && (
          <div className="db-fc__chips flex flex-wrap gap-1.5">
            {specs.map((spec) => (
              <Spec key={spec.key} spec={spec} icon={SPEC_ICONS[spec.key]} />
            ))}
          </div>
        )}

        {/* Price + View details. The main price and the button are the first row of the block,
            the tier list sits under them in an area of fixed height, and the block is pinned to
            the bottom, so every card's price is at the same height (1NOW-11110). The button is
            the card's one control: the click reaches the card, which opens the details. */}
        <div className="mt-auto pt-3.5">
          {notBookable ? (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <span className="db-fc__ptile">
                  <DbIcon name="tag" />
                </span>
                <span className="text-base font-bold leading-tight text-text-color">
                  {onRequest ? "Price on request" : "Price coming soon"}
                </span>
              </div>
              <DetailsButton open={selected} panelId={panelId} ghost />
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex flex-col">
                {hasDiscount && primaryPeriod.kind === "day" && (
                  <span className="text-xs font-medium text-secondary-text-color line-through leading-none">
                    ${formatPrice(increasedPriceNum)}
                  </span>
                )}
                <div className="mt-1 flex items-baseline">
                  <span className="mr-2 text-[13px] font-medium text-secondary-text-color">From</span>
                  <span className="db-fc__price font-extrabold text-text-color">
                    {primaryPeriod.formatted}
                    <span className="ml-1 text-sm font-medium text-secondary-text-color">
                      {primaryPeriod.label}
                    </span>
                  </span>
                </div>
              </div>
              <DetailsButton open={selected} panelId={panelId} />
            </div>
          )}
          {/* Tier area: as tall as the most tier rows any card shows, so a car with one tier
              and a car with three have their main price at the same height. It stays, empty,
              under a car with no tiers. */}
          {slots.rows > 0 && (
            <div
              className={`mt-2.5 pt-2.5 border-t ${
                !notBookable && secondaryPeriods.length > 0 ? "border-text-color/10" : "border-transparent"
              }`}
            >
              <div style={{ minHeight: tierAreaHeight(slots) }}>
                {!notBookable && secondaryPeriods.length > 0 && (
                  <>
                    <div
                      className="grid grid-cols-[auto_auto] justify-start items-center gap-x-2 leading-none"
                      style={{ gridAutoRows: TIER_ROW_PX }}
                    >
                      {secondaryPeriods.map((p) => (
                        <div key={p.days} className="contents">
                          <span className="text-sm font-semibold text-text-color">
                            {p.formatted}
                          </span>
                          <span className="text-xs font-normal text-secondary-text-color">
                            {p.label}
                          </span>
                        </div>
                      ))}
                    </div>
                    {hiddenCount > 0 && (
                      <button
                        type="button"
                        // The whole card is clickable, so expanding in place
                        // has to stop the click reaching it.
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setTiersExpanded(true);
                        }}
                        className="mt-1.5 block text-xs leading-4 font-medium text-secondary-text-color underline underline-offset-2"
                        style={{ height: TIER_MORE_PX - 6 }}
                      >
                        {moreRatesLabel(hiddenCount)}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Description tooltip */}
      {isHovered &&
        description &&
        createPortal(
          <div
            className="fixed bg-text-color text-white text-xs rounded-lg px-3 py-2 shadow-xl z-50 max-w-xs"
            style={{
              top: tooltipPosition.top + 24,
              left: tooltipPosition.left,
            }}
          >
            {description}
          </div>,
          document.body,
        )}
    </article>
  );
};

const SPEC_ICONS: Record<SpecChip["key"], React.ReactNode> = {
  seats: <DbIcon name="user" />,
  doors: <DbIcon name="door" />,
  transmission: <DbIcon name="gear" />,
  color: <DbIcon name="palette" />,
};

/** The word after the value: "4 seats", "2 doors", "Red color". A transmission says itself. */
const chipWord = ({ key, label }: SpecChip) =>
  key === "seats" ? (label === "1" ? "seat" : "seats")
  : key === "doors" ? (label === "1" ? "door" : "doors")
  : key === "color" ? "color"
  : "";

// Icon, value and word on one line. `whitespace-nowrap` keeps a chip whole: a long colour name
// wraps the row, not the chip (1NOW-1098 wrapped a label inside a third of the card).
const Spec: FC<{ spec: SpecChip; icon: React.ReactNode }> = ({ spec, icon }) => (
  <span className="db-spec inline-flex whitespace-nowrap">
    <span className="db-spec__icon shrink-0">{icon}</span>
    <span className="min-w-0 truncate">
      {spec.label}
      {chipWord(spec) && ` ${chipWord(spec)}`}
    </span>
  </span>
);

/** View details / Hide details: a disclosure for the details the card opens. */
const DetailsButton: FC<{ open: boolean; panelId?: string; ghost?: boolean }> = ({ open, panelId, ghost }) => (
  <button
    type="button"
    aria-expanded={open}
    aria-controls={panelId}
    className={`db-btn ${open || ghost ? "db-btn--ghost" : "db-btn--primary"} db-fc__cta w-full`}
  >
    {open ? "Hide details" : "View details"}
    <DbIcon name="chevronDown" className="h-[18px] w-[18px]" />
  </button>
);


export default FleetCard;
